import type { AdminSpeechTopic, ContentCategory, ContentDirection } from '@shared/api.interface';

/**
 * 小红书出图：决定 5 张图各用哪个模板、放哪些主题。
 * 纯逻辑，不碰 DOM，方便单测；随机数从外面传进来。
 */

export type PosterTemplateId = 'A1' | 'A2' | 'A3' | 'A4' | 'A5' | 'B1' | 'B2' | 'C1' | 'C2' | 'C3' | 'C4' | 'C5' | 'C6';

export type PosterTopic = Pick<
  AdminSpeechTopic,
  | 'id'
  | 'title'
  | 'cover_image'
  | 'suitable_grade'
  | 'content_category'
  | 'content_direction'
  | 'estimated_duration'
  | 'content_description'
  | 'script_display'
  | 'ppt_display'
  | 'view_count'
  | 'created_at'
>;

type TopicNeed = 'ppt' | 'script';

export interface PosterTemplateMeta {
  id: PosterTemplateId;
  group: 'A' | 'B' | 'C';
  name: string;
  /** 一张图要放几个主题；C1 用全部主题统计，记 0 */
  count: number;
  /** 只挑有这类预览图的主题 */
  needs?: TopicNeed;
  /** 一个主题一张图，同一组 5 张里尽量不重复 */
  single?: boolean;
  /** 偏向有封面的主题 */
  coverHeavy?: boolean;
  /** 整个主题库里没有可用主题时改用的模板 */
  fallback?: PosterTemplateId;
}

export const POSTER_TEMPLATES: PosterTemplateMeta[] = [
  { id: 'A1', group: 'A', name: '封面墙', count: 12, coverHeavy: true },
  { id: 'A2', group: 'A', name: '筛选展示', count: 6, coverHeavy: true },
  { id: 'A3', group: 'A', name: '列表模式', count: 10, coverHeavy: true },
  { id: 'A4', group: 'A', name: '大卡片', count: 6, coverHeavy: true },
  { id: 'A5', group: 'A', name: '主推大卡', count: 3, coverHeavy: true },
  { id: 'B1', group: 'B', name: '详情弹窗', count: 1, single: true, coverHeavy: true },
  { id: 'B2', group: 'B', name: '资源双联', count: 1, single: true, needs: 'ppt', fallback: 'B1' },
  { id: 'C1', group: 'C', name: '八大分类总览', count: 0 },
  { id: 'C2', group: 'C', name: 'PPT 内页墙', count: 8, needs: 'ppt', fallback: 'A4' },
  { id: 'C3', group: 'C', name: '文稿特写', count: 1, single: true, needs: 'script', fallback: 'B2' },
  { id: 'C4', group: 'C', name: '方向专题页', count: 5, coverHeavy: true },
  { id: 'C5', group: 'C', name: '资源清单', count: 1, single: true },
  { id: 'C6', group: 'C', name: '方向卡片墙', count: 6, coverHeavy: true },
];

export const getTemplateMeta = (id: PosterTemplateId): PosterTemplateMeta =>
  POSTER_TEMPLATES.find((item) => item.id === id) as PosterTemplateMeta;

export type PosterComboId = 'overview' | 'single' | 'mixed' | 'quality' | 'album';

export const POSTER_COMBOS: Array<{ id: PosterComboId; name: string; templates: PosterTemplateId[] }> = [
  { id: 'overview', name: '总览型', templates: ['A4', 'A1', 'A2', 'A3', 'B1'] },
  { id: 'single', name: '单主题型', templates: ['B1', 'B1', 'B1', 'B1', 'B1'] },
  { id: 'mixed', name: '混合型', templates: ['A1', 'B1', 'B1', 'B2', 'A1'] },
  { id: 'quality', name: '内容质感型', templates: ['A4', 'C1', 'C2', 'B2', 'C5'] },
  { id: 'album', name: '专辑型', templates: ['C1', 'C4', 'C6', 'C4', 'A2'] },
];

/** 范围：all 全部 / hot 热门 / new 最新 / 某个分类 */
export type PosterScope = 'all' | 'hot' | 'new' | ContentCategory;

const HOT_OR_NEW_LIMIT = 24;
const ALBUM_DIRECTIONS: ContentDirection[] = ['chinese_spirit', 'chinese_wisdom', 'chinese_feature'];

export interface PosterSlot {
  template: PosterTemplateId;
  topicIds: string[];
  /** A1/A2 高亮的分类 */
  category?: ContentCategory;
  /** C4 / C6 的内容方向 */
  direction?: ContentDirection;
}

type Random = () => number;

const hasNeed = (topic: PosterTopic, need?: TopicNeed) => {
  if (need === 'ppt') return Boolean(topic.ppt_display);
  if (need === 'script') return Boolean(topic.script_display);
  return true;
};

/** 整个主题库里能不能用这个模板（文稿预览一个都没传时，C3 不可用） */
export function isTemplateAvailable(id: PosterTemplateId, topics: PosterTopic[]): boolean {
  const meta = getTemplateMeta(id);
  if (meta.count === 0) return topics.length > 0;
  return topics.some((topic) => hasNeed(topic, meta.needs));
}

export function resolveTemplate(id: PosterTemplateId, topics: PosterTopic[]): PosterTemplateId {
  let current = id;
  const seen = new Set<PosterTemplateId>();
  while (!isTemplateAvailable(current, topics) && !seen.has(current)) {
    seen.add(current);
    const next = getTemplateMeta(current).fallback;
    if (!next) break;
    current = next;
  }
  return current;
}

export function scopeTopics(topics: PosterTopic[], scope: PosterScope): PosterTopic[] {
  if (scope === 'all') return topics.slice();
  if (scope === 'hot') {
    return topics.slice().sort((a, b) => b.view_count - a.view_count).slice(0, HOT_OR_NEW_LIMIT);
  }
  if (scope === 'new') {
    return topics
      .slice()
      .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
      .slice(0, HOT_OR_NEW_LIMIT);
  }
  return topics.filter((topic) => topic.content_category === scope);
}

export function shuffle<T>(items: T[], random: Random): T[] {
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/** 从候选里挑 count 个：先挑没用过的，有封面的优先；不够再用用过的，再放宽到全部主题（allTopics 传候选本身则不放宽） */
function pickTopics(
  candidates: PosterTopic[],
  allTopics: PosterTopic[],
  count: number,
  meta: PosterTemplateMeta,
  used: Set<string>,
  random: Random,
): PosterTopic[] {
  const eligible = (list: PosterTopic[]) => list.filter((topic) => hasNeed(topic, meta.needs));
  const rank = (list: PosterTopic[]) => {
    const shuffled = shuffle(list, random);
    if (!meta.coverHeavy) return shuffled;
    return [...shuffled.filter((t) => t.cover_image), ...shuffled.filter((t) => !t.cover_image)];
  };
  const picked: PosterTopic[] = [];
  const take = (list: PosterTopic[]) => {
    for (const topic of list) {
      if (picked.length >= count) return;
      if (!picked.includes(topic)) picked.push(topic);
    }
  };
  const scoped = eligible(candidates);
  take(rank(scoped.filter((t) => !used.has(t.id))));
  take(rank(scoped.filter((t) => used.has(t.id))));
  take(rank(eligible(allTopics).filter((t) => !used.has(t.id))));
  take(rank(eligible(allTopics)));
  return picked;
}

export interface BuildContext {
  topics: PosterTopic[];
  scope: PosterScope;
  random: Random;
}

const isDirectionTemplate = (id: PosterTemplateId) => id === 'C4' || id === 'C6';

/** 生成一张图；used 里是同组其它图已经用掉的主题 */
export function buildSlot(
  requested: PosterTemplateId,
  context: BuildContext,
  used: Set<string>,
  options: { direction?: ContentDirection; category?: ContentCategory } = {},
): PosterSlot {
  const { topics, scope, random } = context;
  const template = resolveTemplate(requested, topics);
  const meta = getTemplateMeta(template);
  const scopeCategory = scope !== 'all' && scope !== 'hot' && scope !== 'new' ? scope : undefined;

  if (template === 'C1') {
    return { template, topicIds: [] };
  }

  let candidates = scopeTopics(topics, scope);
  const slot: PosterSlot = { template, topicIds: [] };

  if (isDirectionTemplate(template)) {
    const directions = ALBUM_DIRECTIONS.filter((d) => topics.some((t) => t.content_direction === d));
    const direction =
      options.direction && directions.includes(options.direction)
        ? options.direction
        : directions[Math.floor(random() * directions.length)] || topics[0]?.content_direction;
    slot.direction = direction;
    candidates = topics.filter((t) => t.content_direction === direction);
    const picked = pickTopics(candidates, candidates, meta.count, meta, used, random);
    slot.topicIds = picked.map((t) => t.id);
    return slot;
  }

  const category = scopeCategory || options.category;
  if (category && (template === 'A1' || template === 'A2')) {
    slot.category = category;
    candidates = topics.filter((t) => t.content_category === category);
  }

  // 高亮了某个分类的封面墙/筛选图只放这个分类，宁可少放几张
  const pool = slot.category ? candidates : topics;
  const picked = pickTopics(candidates, pool, meta.count, meta, used, random);
  slot.topicIds = picked.map((t) => t.id);
  if (template === 'A2' && !slot.category) {
    slot.category = picked[0]?.content_category;
  }
  if (meta.single) picked.forEach((t) => used.add(t.id));
  return slot;
}

export function buildCombo(comboId: PosterComboId, context: BuildContext): PosterSlot[] {
  const combo = POSTER_COMBOS.find((item) => item.id === comboId) || POSTER_COMBOS[0];
  const used = new Set<string>();
  let albumIndex = 0;
  return combo.templates.map((template, index) => {
    const options: { direction?: ContentDirection; category?: ContentCategory } = {};
    if (isDirectionTemplate(template) && comboId === 'album') {
      options.direction = ALBUM_DIRECTIONS[albumIndex % ALBUM_DIRECTIONS.length];
      albumIndex += 1;
    }
    // 混合型最后一张封面墙换一个分类，和第一张区分开
    if (comboId === 'mixed' && template === 'A1' && index > 0) {
      const categories = Array.from(new Set(context.topics.map((t) => t.content_category)));
      options.category = categories[Math.floor(context.random() * categories.length)];
    }
    return buildSlot(template, context, used, options);
  });
}

/** 单独换某一张的模板或主题，避开其它几张已用的单主题 */
export function rebuildSlot(
  slots: PosterSlot[],
  index: number,
  template: PosterTemplateId,
  context: BuildContext,
): PosterSlot[] {
  const used = new Set<string>();
  slots.forEach((slot, i) => {
    if (i !== index && getTemplateMeta(slot.template).single) slot.topicIds.forEach((id) => used.add(id));
  });
  // 换主题时也避开自己当前的主题
  if (slots[index]?.template === template && getTemplateMeta(template).single) {
    slots[index].topicIds.forEach((id) => used.add(id));
  }
  const previous = slots[index];
  const options =
    previous && previous.template === template
      ? { direction: previous.direction, category: previous.category }
      : {};
  const next = slots.slice();
  next[index] = buildSlot(template, context, used, options);
  return next;
}
